import { API_SERVER_URL } from '../../resources/constants';

/**
 * HO-152: webovy klient chatoveho WebSocketu (`/ws/chat`).
 *
 * Web dosud jel VYHRADNE na dotazovani - historie po 5 s, metadata kanalu po
 * 15 s. Behem zasahu to znamena, ze velitel u stolu vidi odpoved hasice az o
 * nekolik sekund pozdeji, nevidi vubec, ze nekdo prave pise, a kazda otevrena
 * zalozka posila 12 pozadavku za minutu i kdyz se nic nedeje. Mobil je na
 * socketu od HO-144; tenhle modul dela na webu totez.
 *
 * <b>Protokol se NEVYMYSLI ZNOVU</b> - je to tyz ramcovy dialekt, ktery obsluhuje
 * `ChatWebSocketHandler` na backendu a `chatDoruceni.ts` v mobilu:
 * - klient -> server: `ODBER`, `PISU`, `PONG`
 * - server -> klient: `ZMENA`, `PISE`, `PING`, `CHYBA`
 * Obalka je vzdy `{ typ, konverzaceId, data }`.
 *
 * Modul je zamerne BEZ Reactu - stavovy automat spojeni se tak da testovat
 * s podvrzenym socketem a bez vykreslovani. React vrstva je `useChatSocket`.
 */

/** Verze protokolu v query - server ji uklada a neznamou odmita kodem 4409. */
const WS_PROTOKOL_VERZE = '1';

/** Server skrti `PISU` na jeden za 5 s (`ChatWebSocketHandler.PISU_THROTTLE`),
 *  takze klient posila s rezervou - jinak by zahozeny ramec jen zbytecne
 *  odecitalo z limitu prichozich zprav. */
const PISU_ODSTUP_MS = 5500;

/** Jak dlouho drzet "pise", kdyz dalsi ramec nedorazi. Pisici klient posila
 *  nejpozdeji po 5 s, 7 s je tedy jedno zmeskane kolo plus rezerva. Kratsi
 *  hodnota by indikatorem blikala mezi ramci. */
const PLATNOST_PISE_MS = 7000;

/** Uklid prosle indikace psani. Bezi jen kdyz spojeni zije. */
const UKLID_PISE_MS = 1000;

const ZAKLADNI_ODSTUP_MS = 1000;
const MAX_ODSTUP_MS = 30000;

/** Po kolika neuspesich za sebou se stav hlasi jako POLLING. Nezastavuje to
 *  pokusy - proxy bez podpory Upgrade se muze kdykoli prekonfigurovat a chat
 *  se ma sam vratit k zivotu. Meni to jen to, co vidi stranka: prestane cekat
 *  na socket a zapne rychlejsi dotazovani. */
const NEUSPECHU_PRED_POLLINGEM = 3;

/** Kolikrat po sobe tolerovat 4401 (relace neplatna). Webova relace ROTUJE
 *  refresh token, takze ticket navazany na starou radku prestane platit pri
 *  kazdem obnoveni prihlaseni a server socket do 30 s zavre - to je NORMALNI
 *  provozni stav, ne chyba, a resi se novym ticketem z AKTUALNIHO tokenu.
 *  Opakovane 4401 uz ale znamena skutecne neplatnou relaci a dalsi pokusy by
 *  jen tloukly na zavrene dvere. */
const MAX_RELACE_NEPLATNA = 3;

/** Kody, po kterych nema smysl zkouset znovu: clenstvi zaniklo, uzivatel se
 *  odhlasil, nebo server nasi verzi protokolu nezna. Zadny z nich se novym
 *  ticketem nespravi. */
const TERMINALNI_KODY = new Set([4403, 4408, 4409]);

/** Prilis mnoho zprav - server nas umlcel, kratky reconnect by to jen zopakoval. */
const KOD_PRILIS_MNOHO_ZPRAV = 4429;

const KOD_RELACE_NEPLATNA = 4401;

export type StavChatSpojeni = 'PRIPOJUJI' | 'SOCKET' | 'POLLING';

/** Podmnozina `WebSocket`, kterou modul opravdu pouziva - v testech se podvrhne. */
export interface WebSocketLike {
    send(data: string): void;
    close(kod?: number, duvod?: string): void;
    onopen: (() => void) | null;
    onmessage: ((ev: { data: unknown }) => void) | null;
    onclose: ((ev: { code: number; reason?: string }) => void) | null;
    onerror: ((ev?: unknown) => void) | null;
}

export interface ChatSpojeniNastaveni {
    konverzaceId: number;
    /** Vola se PRED KAZDYM pokusem - ticket je jednorazovy a plati 60 s, takze
     *  se nesmi cachovat, a po rotaci tokenu musi vzniknout z toho AKTUALNIHO. */
    ziskejTicket: () => Promise<string>;
    /** Neco se v konverzaci zmenilo - stranka si ma dotahnout zpravy. */
    naZmenu: () => void;
    /** Aktualni seznam jmen, ktera prave pisi (bez nas). Prazdne pole = nikdo. */
    naPise: (jmena: string[]) => void;
    naStav: (stav: StavChatSpojeni) => void;
    /** Zaklad adresy backendu. Vychozi je `API_SERVER_URL`, a kdyz neni
     *  nastavena (FE bezi na stejnem puvodu jako API), adresa stranky. */
    zakladUrl?: string;
    tovarna?: (url: string) => WebSocketLike;
    /** Injektovatelna nahoda kvuli jitteru - test potrebuje deterministicke casy. */
    nahodne?: () => number;
}

export interface ChatSpojeni {
    zavri(): void;
    /** Uzivatel prave pise. Skrti se, takze se da volat pri kazdem stisku klavesy. */
    oznamPsani(): void;
}

/**
 * Sestavi adresu socketu. `http` -> `ws`, `https` -> `wss`; kdyz backend adresa
 * neni nastavena, jede se na stejny puvod jako stranka (nginx FE staticky
 * neproxuje, takze v tom pripade API a FE sdili puvod).
 */
export function sestavWsUrl(zaklad: string, ticket: string): string {
    const bezLomitka = zaklad.replace(/\/+$/, '');
    const wsZaklad = bezLomitka.replace(/^http/i, (m) => (m === 'HTTP' ? 'WS' : 'ws'));
    return `${wsZaklad}/ws/chat?ticket=${encodeURIComponent(ticket)}&v=${WS_PROTOKOL_VERZE}`;
}

/** Exponencialni odstup s jitterem. Jitter je povinny, ne kosmeticky: po
 *  restartu backendu se jinak vsechny otevrene zalozky pripoji ve stejny
 *  okamzik a shodi ho znovu. */
function odstupPokusu(pokus: number, nahodne: () => number): number {
    const zakladni = Math.min(ZAKLADNI_ODSTUP_MS * Math.pow(2, Math.max(0, pokus - 1)), MAX_ODSTUP_MS);
    return Math.round(zakladni * (0.75 + nahodne() * 0.5));
}

export function pripojChat(nastaveni: ChatSpojeniNastaveni): ChatSpojeni {
    const {
        konverzaceId,
        ziskejTicket,
        naZmenu,
        naPise,
        naStav,
        zakladUrl = API_SERVER_URL || window.location.origin,
        tovarna = (url: string) => new WebSocket(url) as unknown as WebSocketLike,
        nahodne = Math.random,
    } = nastaveni;

    let zavreno = false;
    let socket: WebSocketLike | null = null;
    let neuspechuPoRade = 0;
    let relaceNeplatnaPoRade = 0;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let uklidTimer: ReturnType<typeof setInterval> | null = null;
    let poslednePoslanoPisu = 0;
    let hlasenyStav: StavChatSpojeni | null = null;
    /** Kdo prave pise: uzivatelId -> jmeno a dokdy indikace plati. */
    const pisici = new Map<number, { jmeno: string; platiDoMs: number }>();

    const hlasStav = (stav: StavChatSpojeni) => {
        if (hlasenyStav === stav) {
            return;
        }
        hlasenyStav = stav;
        naStav(stav);
    };

    const vycistiPisici = () => {
        if (pisici.size === 0) {
            return;
        }
        pisici.clear();
        naPise([]);
    };

    const oznamPisici = () => {
        naPise(Array.from(pisici.values()).map((p) => p.jmeno));
    };

    const spustUklidPisicich = () => {
        if (uklidTimer) {
            return;
        }
        uklidTimer = setInterval(() => {
            const ted = Date.now();
            let zmena = false;
            pisici.forEach((hodnota, id) => {
                if (hodnota.platiDoMs <= ted) {
                    pisici.delete(id);
                    zmena = true;
                }
            });
            if (zmena) {
                oznamPisici();
            }
        }, UKLID_PISE_MS);
    };

    const zastavUklidPisicich = () => {
        if (uklidTimer) {
            clearInterval(uklidTimer);
            uklidTimer = null;
        }
    };

    const posli = (ramec: { typ: string; konverzaceId: number | null; data: unknown }) => {
        if (!socket) {
            return;
        }
        try {
            socket.send(JSON.stringify(ramec));
        } catch {
            // Odeslani na uz zavirajici se socket nesmi shodit zpracovani ramce -
            // zavreni si vzapeti obslouzi `onclose`.
        }
    };

    const zpracujRamec = (surova: unknown) => {
        if (typeof surova !== 'string') {
            return;
        }
        let obalka: { typ?: string; konverzaceId?: number | null; data?: unknown };
        try {
            obalka = JSON.parse(surova);
        } catch {
            return;
        }

        if (obalka.typ === 'PING') {
            // MUSI se odpovedet. Server neposila protokolovy control ramec (opcode
            // 0x9), na ktery by transportni vrstva odpovedela sama, ale APLIKACNI
            // textovy ramec {"typ":"PING"}. Bez odpovedi server po trech kolech
            // spojeni zavre kodem 1001 - v mobilu to znamenalo, ze zadny socket
            // nezil dele nez dve minuty, a chat se "obcas kousal".
            posli({ typ: 'PONG', konverzaceId: null, data: null });
            return;
        }
        if (obalka.typ === 'ZMENA' && obalka.konverzaceId === konverzaceId) {
            // Jitter PRED dotazenim: bez nej si po jedne zprave sahnou vsichni
            // pripojeni ve stejnou milisekundu pro tutez stranku historie.
            const zpozdeni = Math.round(nahodne() * 400);
            setTimeout(() => {
                if (!zavreno) {
                    naZmenu();
                }
            }, zpozdeni);
            return;
        }
        if (obalka.typ === 'PISE' && obalka.konverzaceId === konverzaceId) {
            const data = obalka.data as { uzivatelId?: number; jmeno?: string } | null | undefined;
            if (!data || typeof data.uzivatelId !== 'number') {
                return;
            }
            pisici.set(data.uzivatelId, {
                jmeno: data.jmeno ?? 'Někdo',
                platiDoMs: Date.now() + PLATNOST_PISE_MS,
            });
            oznamPisici();
            return;
        }
        if (obalka.typ === 'CHYBA') {
            const data = obalka.data as { kod?: string } | undefined;
            // Web odebira JEDINOU konverzaci, takze `prilis_mnoho_odberu` tu nastat
            // nemuze a `not_found` znamena, ze kanal zmizel - opakovany ODBER by
            // vratil tutez chybu. Socket se necha dobehnout a stranka spadne na
            // dotazovani, ktere si neexistujici kanal vyresi samo (404 -> chybovy
            // stav stranky).
            if (data?.kod === 'not_found') {
                hlasStav('POLLING');
            }
        }
    };

    const naplanujReconnect = (odstupMs: number) => {
        if (zavreno || reconnectTimer) {
            return;
        }
        reconnectTimer = setTimeout(() => {
            reconnectTimer = null;
            void pripoj();
        }, odstupMs);
    };

    const pripoj = async (): Promise<void> => {
        if (zavreno || socket) {
            return;
        }
        hlasStav(neuspechuPoRade >= NEUSPECHU_PRED_POLLINGEM ? 'POLLING' : 'PRIPOJUJI');

        let ticket: string;
        try {
            ticket = await ziskejTicket();
        } catch {
            // Nejcastejsi duvod je 403 (uzivatel neni clenem chatu) nebo 429
            // (limit vydavani ticketu) - obojim se nema smysl trapit, dotazovani
            // funguje dal.
            neuspechuPoRade += 1;
            if (neuspechuPoRade >= NEUSPECHU_PRED_POLLINGEM) {
                hlasStav('POLLING');
            }
            naplanujReconnect(odstupPokusu(neuspechuPoRade, nahodne));
            return;
        }
        if (zavreno) {
            return;
        }

        let novy: WebSocketLike;
        try {
            novy = tovarna(sestavWsUrl(zakladUrl, ticket));
        } catch {
            neuspechuPoRade += 1;
            naplanujReconnect(odstupPokusu(neuspechuPoRade, nahodne));
            return;
        }
        socket = novy;

        novy.onopen = () => {
            if (zavreno) {
                return;
            }
            neuspechuPoRade = 0;
            hlasStav('SOCKET');
            // ODBER server VZDY NAHRAZUJE celym seznamem, nepripocitava.
            posli({ typ: 'ODBER', konverzaceId: null, data: { konverzace: [konverzaceId] } });
            // Socket mohl byt chvili mrtvy, aniz to klient poznal - zprava, ktera
            // mezitim dorazila, by se ukazala az po dalsi ZMENE.
            naZmenu();
            spustUklidPisicich();
        };

        novy.onmessage = (ev) => {
            if (!zavreno) {
                zpracujRamec(ev?.data);
            }
        };

        novy.onerror = () => {
            // `onclose` prijde vzdy po `onerror`, takze se reconnect planuje jen tam.
        };

        novy.onclose = (ev) => {
            socket = null;
            zastavUklidPisicich();
            // Kontrola `zavreno` PRED jakymkoli hlasenim ven: nase vlastni zavreni
            // vyvola `onclose` taky a stranka uz je pryc. Bez toho by odchod z
            // chatu jeste ohlasil "POLLING" a prazdne pisici do odmontovane
            // komponenty - stav, ktery uz nikdo necte a ktery mate diagnostiku.
            if (zavreno) {
                return;
            }
            vycistiPisici();
            const kod = ev?.code ?? 1006;

            if (TERMINALNI_KODY.has(kod)) {
                // Clenstvi zaniklo, odhlaseni, nebo neznama verze protokolu - dalsi
                // ticket by dopadl stejne.
                hlasStav('POLLING');
                return;
            }
            if (kod === KOD_RELACE_NEPLATNA) {
                relaceNeplatnaPoRade += 1;
                if (relaceNeplatnaPoRade >= MAX_RELACE_NEPLATNA) {
                    hlasStav('POLLING');
                    return;
                }
                // Nejcasteji jen rotace refresh tokenu - novy ticket vznikne z toho
                // aktualniho, takze se pripojujeme rychle a bez eskalace odstupu.
                naplanujReconnect(odstupPokusu(1, nahodne));
                return;
            }
            relaceNeplatnaPoRade = 0;

            if (kod === KOD_PRILIS_MNOHO_ZPRAV) {
                hlasStav('POLLING');
                naplanujReconnect(MAX_ODSTUP_MS);
                return;
            }

            neuspechuPoRade += 1;
            if (neuspechuPoRade >= NEUSPECHU_PRED_POLLINGEM) {
                hlasStav('POLLING');
            }
            naplanujReconnect(odstupPokusu(neuspechuPoRade, nahodne));
        };
    };

    void pripoj();

    return {
        zavri() {
            zavreno = true;
            if (reconnectTimer) {
                clearTimeout(reconnectTimer);
                reconnectTimer = null;
            }
            zastavUklidPisicich();
            pisici.clear();
            const k = socket;
            socket = null;
            if (k) {
                try {
                    k.close(1000, 'odchod ze stranky');
                } catch {
                    // Zavirani uz zavreneho socketu neni chyba, kterou by mel resit volajici.
                }
            }
        },
        oznamPsani() {
            if (!socket || hlasenyStav !== 'SOCKET') {
                return;
            }
            const ted = Date.now();
            if (ted - poslednePoslanoPisu < PISU_ODSTUP_MS) {
                return;
            }
            poslednePoslanoPisu = ted;
            posli({ typ: 'PISU', konverzaceId, data: null });
        },
    };
}
